import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import { ArrowLeft, Download, Printer, Loader2 } from "lucide-react";
import { api, formatISK } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MARKETING_TOUR_IDS = ["buggy", "ice_cave"];

// Price overrides for the marketing poster (what we want to advertise),
// which can differ from the tour's default "from" price.
const PRICE_OVERRIDE = {
  buggy: { price: 60000, unit: "buggy (1 rider)" },
};

function PosterCard({ tour }) {
  const cardRef = useRef(null);
  const [qr, setQr] = useState("");
  const [downloading, setDownloading] = useState(false);
  const bookingUrl = `${window.location.origin}/tours/${tour.id}`;
  const ov = PRICE_OVERRIDE[tour.id] || {};
  const displayPrice = ov.price ?? tour.adult_price;
  const displayUnit = ov.unit ?? tour.price_unit ?? "person";

  useEffect(() => {
    QRCode.toDataURL(bookingUrl, { width: 640, margin: 1, errorCorrectionLevel: "M", color: { dark: "#0C0C0E", light: "#ffffff" } })
      .then(setQr)
      .catch(() => {});
  }, [bookingUrl]);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#0C0C0E", scale: 3, useCORS: true });
      const link = document.createElement("a");
      link.download = `immersion-${tour.id}-qr-poster.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Poster downloaded");
    } catch (e) {
      toast.error("Could not generate the image");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid={`marketing-card-${tour.id}`} className="flex flex-col">
      {/* Printable poster card */}
      <div ref={cardRef} data-testid={`poster-${tour.id}`} className="marketing-poster relative bg-[#0C0C0E] border border-[#c9a24b]/40 overflow-hidden" style={{ width: 420 }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 50% 0%, rgba(201,162,75,0.10), transparent 60%)" }} />
        <div className="relative px-10 pt-10 pb-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.42em] text-[#c9a24b] mb-1">Immersion Festival</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-8">Iceland · Oct 1–5</p>

          <h2 className="font-display font-extrabold uppercase tracking-tight text-[2rem] leading-[0.95] text-white">{tour.name}</h2>
          {tour.tagline && <p className="text-sm text-gray-400 mt-3 italic">{tour.tagline}</p>}

          <div className="mt-6 inline-flex flex-col items-center">
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500">From</span>
            <span className="text-3xl font-display font-bold text-white mt-1">{Number(displayPrice || 0).toLocaleString("en-US")} ISK</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">/ {displayUnit}</span>
          </div>

          <div className="mt-8 flex justify-center">
            <div className="bg-white p-4 rounded-sm">
              {qr ? <img src={qr} alt={`${tour.name} booking QR`} data-testid={`poster-qr-${tour.id}`} className="w-52 h-52 block" /> : <div className="w-52 h-52" />}
            </div>
          </div>

          <p className="mt-6 text-sm uppercase tracking-[0.28em] text-[#e3c67d] font-bold">Scan to book</p>
          <p className="mt-2 text-[11px] text-gray-500 tracking-wide">Point your phone camera at the code</p>
        </div>
      </div>

      {/* Actions (not part of the poster) */}
      <div className="flex items-center gap-3 mt-4 no-print" style={{ width: 420 }}>
        <button data-testid={`download-poster-${tour.id}`} onClick={download} disabled={downloading || !qr}
          className="flex-1 bg-white text-black hover:bg-gray-200 disabled:opacity-50 uppercase tracking-[0.15em] text-xs font-bold py-3 px-4 transition-colors inline-flex items-center justify-center gap-2">
          {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PNG
        </button>
        <a data-testid={`download-qr-${tour.id}`} href={qr || "#"} download={`immersion-${tour.id}-qr.png`}
          className="flex-1 border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3 px-4 transition-colors inline-flex items-center justify-center gap-2">
          <Download size={15} /> QR only
        </a>
      </div>
      <p className="text-[11px] text-gray-600 mt-3 break-all no-print" style={{ width: 420 }}>Links to: {bookingUrl}</p>
    </div>
  );
}

export default function MarketingQR() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tours").then((r) => {
      const all = r.data.tours || [];
      const byId = Object.fromEntries(all.map((t) => [t.id, t]));
      setTours(MARKETING_TOUR_IDS.map((id) => byId[id]).filter(Boolean));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="marketing-qr-page" className="min-h-screen bg-black text-white">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } .marketing-poster { break-inside: avoid; } }`}</style>

      <div className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center gap-4 no-print">
        <Link to="/admin" data-testid="back-to-admin" className="text-gray-400 hover:text-white inline-flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Dashboard</Link>
        <span className="font-display font-bold tracking-tight text-lg ml-2">Marketing QR Codes</span>
        <div className="ml-auto flex items-center gap-3">
          <button data-testid="print-all-btn" onClick={() => window.print()} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2"><Printer size={15} /> Print all</button>
          <button data-testid="marketing-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="text-gray-500 hover:text-white text-sm uppercase tracking-[0.15em]">Logout</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <p className="text-gray-400 text-sm max-w-xl mb-10 no-print">Print these poster cards or download them as images for flyers, social posts and signage. Scanning a code opens that tour's booking page directly — on whichever site this admin is running.</p>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500"><Loader2 size={18} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="flex flex-wrap gap-12" data-testid="marketing-cards">
            {tours.map((t) => <PosterCard key={t.id} tour={t} />)}
          </div>
        )}
      </div>
    </div>
  );
}
